import React, { useState, useEffect } from 'react';
import { getTeamPerformance, getEmployeeDeliverables, formatIQD } from '../utils/api';
import toast from 'react-hot-toast';
import {
  Users, Activity, DollarSign, Target, Award,
  AlertTriangle, ArrowDown, ArrowUp, Calendar, Video
} from 'lucide-react';


const S = {
  card: { background: 'var(--card-bg, white)', borderRadius: 12, padding: 20, border: '1px solid var(--card-border, #E2E8F0)', boxShadow: 'var(--shadow-card, 0 4px 6px -1px rgb(0 0 0 / 0.05))' },
  metricValue: { fontSize: 24, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', marginTop: 8 },
  metricLabel: { fontSize: 13, color: 'var(--text-gray, #64748B)', display: 'flex', alignItems: 'center', gap: 6 },
  th: { padding: '14px 16px', fontSize: 13, fontWeight: 700, color: 'var(--text-gray, #64748B)', borderBottom: '2px solid var(--border-light, #E2E8F0)' },
  td: { padding: '14px 16px', fontSize: 13, color: 'var(--text-dark-h, #0F1E33)', borderBottom: '1px solid var(--border-light, #E2E8F0)', verticalAlign: 'middle' },
};

function KPICard({ label, value, icon, color, subValue = null }) {
  return (
    <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!shadow-none" style={{ ...S.card, borderTop: `4px solid ${color}` }}>
      <div style={S.metricLabel}>
        {React.cloneElement(icon, { size: 16, color })}
        {label}
      </div>
      <div style={S.metricValue}>{value}</div>
      {subValue && <div className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-muted, #94A3B8)', marginTop: 4 }}>{subValue}</div>}
    </div>
  );
}

export default function TeamPerformancePage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ performance: [], summary: {} });
  const [month, setMonth] = useState(new Date().toISOString().substring(0, 7));
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [empLogs, setEmpLogs] = useState(null);
  const [filterDate, setFilterDate] = useState('all');

  const uniqueDates = empLogs ? [...new Set(empLogs.map(log => {
      const d = new Date(log.delivery_date || log.created_at);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }))].sort((a, b) => b.localeCompare(a)) : [];
  
  const filteredLogs = empLogs ? (filterDate === 'all' ? empLogs : empLogs.filter(log => {
      const d = new Date(log.delivery_date || log.created_at);
      const logDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return logDate === filterDate;
  })) : null;

  useEffect(() => {
    loadData();
  }, [month]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getTeamPerformance({ month });
      setData(res);
    } catch (err) {
      toast.error('فشل في تحميل بيانات الفريق');
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (emp) => {
    setSelectedEmployee(emp);
    setFilterDate('all');
    setEmpLogs(null);
    try {
      const logs = await getEmployeeDeliverables(emp.id, { month });
      setEmpLogs(logs);
    } catch (err) {
      toast.error('فشل في جلب تقرير المهام المنجزة');
      setSelectedEmployee(null);
    }
  };

  const { performance, summary } = data;

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-dark dark:text-gray-100 flex items-center gap-3">
            <Activity className="text-primary" size={28} />
            إدارة الفريق والإنتاجية
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">متابعة الثواني المنجزة وحساب البونص والخصومات تلقائياً لكل موظف.</p>
        </div>
        <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{ display: 'flex', gap: 10, alignItems: 'center', background: 'var(--bg-flat-white, white)', padding: '6px 14px', borderRadius: 10, border: '1px solid var(--border-light, #E2E8F0)' }}>
          <Calendar size={18} color="var(--text-gray, #64748B)" />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-transparent"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'Tajawal', fontSize: 14, fontWeight: 700, color: 'var(--text-dark-h, #0F1E33)' }}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center text-gray-400 font-medium">جاري التحميل...</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <KPICard
              label="إجمالي الرواتب الأساسية"
              value={formatIQD(summary.total_salaries || 0)}
              color="#3B82F6"
              icon={<Users />}
            />
            <KPICard
              label="إجمالي البونص (المكافآت)"
              value={formatIQD(summary.total_bonuses || 0)}
              color="#10B981"
              icon={<ArrowUp />}
            />
            <KPICard
              label="إجمالي الخصومات"
              value={formatIQD(summary.total_deductions || 0)}
              color="#EF4444"
              icon={<ArrowDown />}
            />
            <KPICard
              label="إجمالي الصافي المتوقع"
              value={formatIQD(summary.total_net_payout || 0)}
              color="#8B5CF6"
              icon={<DollarSign />}
              subValue="راتب أساسي ± البونصات والخصومات"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20 }}>
            {/* Leaderboard Table */}
            <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
              <h2 className="text-lg font-bold text-dark dark:text-gray-100 mb-4 flex items-center gap-2">
                <Award className="text-yellow-500" size={20} />
                ليدربورد الأداء — {month}
              </h2>

              <div className="overflow-x-auto">
                <table style={{ width: '100%', textAlign: 'right', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: 60, textAlign: 'center' }}>الترتيب</th>
                      <th style={S.th}>الموظف</th>
                      <th style={S.th}>الوظيفة</th>
                      <th style={S.th}>المنجز (ثانية)</th>
                      <th style={S.th}>المستهدف (Baseline)</th>
                      <th style={S.th}>الزيادة / النقص</th>
                      <th style={S.th}>المكافأة / الخصم</th>
                      <th style={S.th}>الراتب الصافي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.length === 0 ? (
                      <tr><td colSpan={8} className="dark:!text-gray-400" style={{ ...S.td, textAlign: 'center', color: 'var(--text-gray, #64748B)' }}>لا يوجد بيانات</td></tr>
                    ) : (
                      performance.map((emp, idx) => {
                        const isUnderperforming = (emp.difference < 0 && Math.abs(emp.difference) > (emp.baseline_seconds * 0.2));
                        return (
                          <tr key={emp.id} style={{ transition: 'background 0.2s' }} className={`hover:bg-gray-50 dark:hover:bg-white/5 ${idx === 0 ? 'bg-[#FEFCE8] dark:!bg-yellow-600/20' : 'bg-transparent dark:bg-white/5'}`}>
                            <td style={{ ...S.td, textAlign: 'center', fontWeight: 800, color: idx < 3 ? '#F59E0B' : 'var(--text-gray, #64748B)' }}>
                              #{idx + 1}
                            </td>
                            <td style={{ ...S.td, fontWeight: 700 }}>
                              <div className="flex items-center justify-between group">
                                <div className="flex items-center gap-2">
                                  {emp.name}
                                  {isUnderperforming && (
                                    <span title="تأخر كبير عن المستهدف" className="flex items-center text-danger bg-danger/10 p-1 rounded-full">
                                      <AlertTriangle size={14} />
                                    </span>
                                  )}
                                </div>
                                <button onClick={() => viewDetails(emp)} className="opacity-0 group-hover:opacity-100 text-xs px-2 py-1 bg-primary/10 text-primary hover:bg-primary/20 rounded transition-all">
                                  عرض التقرير
                                </button>
                              </div>
                            </td>
                            <td style={S.td}>{emp.role}</td>
                            <td style={{ ...S.td, fontWeight: 800, color: '#1E40AF' }}>
                              {emp.delivered_seconds} ث
                            </td>
                            <td className="dark:!text-gray-400" style={{ ...S.td, color: 'var(--text-gray, #64748B)' }}>
                              {emp.baseline_seconds} ث
                            </td>
                            <td style={{ ...S.td, fontWeight: 700, color: emp.difference > 0 ? '#10B981' : (emp.difference < 0 ? '#EF4444' : 'var(--text-gray, #64748B)') }}>
                              <div className="flex items-center gap-1">
                                {emp.difference > 0 ? <ArrowUp size={14} /> : (emp.difference < 0 ? <ArrowDown size={14} /> : '- ')}
                                {Math.abs(emp.difference)} ث
                              </div>
                            </td>
                            <td style={{ ...S.td, fontWeight: 700, color: emp.bonus_deduction > 0 ? '#10B981' : (emp.bonus_deduction < 0 ? '#EF4444' : 'var(--text-gray, #64748B)') }}>
                              {emp.bonus_deduction > 0 ? '+' : (emp.bonus_deduction < 0 ? '-' : '')} {formatIQD(Math.abs(emp.bonus_deduction))}
                            </td>
                            <td style={{ ...S.td, fontWeight: 800 }}>
                              {formatIQD(emp.net_salary_iqd)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Selected Employee Details Modal */}
          {selectedEmployee && (
            <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedEmployee(null)}>
              <div className="bg-white dark:bg-dark-deeper rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-gray-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-white/5/50 rounded-t-2xl">
                  <div>
                    <h3 className="font-bold text-lg text-dark dark:text-gray-100 flex items-center gap-2">
                      <Video className="text-primary" size={20} />
                      التسليمات المنجزة للموظف: {selectedEmployee.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      الثواني المنجزة: {selectedEmployee.delivered_seconds} ثانية — خلال {month}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    {uniqueDates.length > 0 && (
                      <select
                        value={filterDate}
                        onChange={(e) => setFilterDate(e.target.value)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-dark-deeper text-sm outline-none text-dark dark:text-gray-100"
                        title="فلترة حسب اليوم"
                      >
                        <option value="all">كل الأيام</option>
                        {uniqueDates.map(date => (
                          <option key={date} value={date}>{date}</option>
                        ))}
                      </select>
                    )}
                    <button onClick={() => setSelectedEmployee(null)} className="p-2 hover:bg-gray-200 dark:bg-white/10 rounded-lg transition-colors text-dark dark:text-gray-100">
                      ✕
                    </button>
                  </div>
                </div>
                <div className="p-5 overflow-y-auto custom-scrollbar flex-1">
                  {!empLogs ? (
                    <div className="text-center py-10 text-gray-400">جاري تحميل التقرير...</div>
                  ) : filteredLogs.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 text-lg">لا توجد مهام منجزة في {filterDate === 'all' ? 'هذا الشهر' : 'هذا اليوم'}</div>
                  ) : (
                    <table style={{ width: '100%', textAlign: 'right', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={S.th}>العميل</th>
                          <th style={S.th}>عنوان التسليم</th>
                          <th style={S.th}>الثواني (المدة)</th>
                          <th style={S.th}>النوع</th>
                          <th style={S.th}>تاريخ الإكمال</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map(log => (
                          <tr key={log.id} className="hover:bg-gray-50 dark:bg-white/5 dark:hover:bg-white/5 transition-colors">
                            <td style={S.td}>{!log.client_id ? <span className="text-gray-400 text-xs bg-gray-100 dark:bg-white/10 px-2 py-1 rounded">داخلي (الوكالة)</span> : log.client_name}</td>
                            <td style={{ ...S.td, fontWeight: 700 }}>{log.title}</td>
                            <td style={{ ...S.td, fontWeight: 800, color: '#10B981' }}>
                              <span className="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-md text-xs inline-flex items-center gap-1">
                                {log.total_seconds_used} ثانية
                                {log.employee_points_multiplier > 1 && (
                                  <span title={`بونص x${log.employee_points_multiplier}`} style={{ display: 'inline-flex', alignItems: 'center', background: '#FEF3C7', color: '#B45309', padding: '0px 4px', borderRadius: 4, fontSize: 10, marginRight: 4 }}>⚡x{log.employee_points_multiplier}</span>
                                )}
                              </span>
                            </td>
                            <td style={S.td}><span className="text-primary bg-primary/10 px-2 py-1 rounded font-medium text-xs">{log.video_type || 'فيديو'}</span></td>
                            <td className="dark:!text-gray-400" style={{ ...S.td, color: 'var(--text-gray, #64748B)' }}>
                              <div>{new Date(log.delivery_date || log.created_at).toLocaleDateString('ar-EG', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}</div>
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{new Date(log.delivery_date || log.created_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
